/**
 * ClassifierPanel — Phase 1.4 verdict + signals + per-asset consent flip.
 *
 * Extracted from BrandAssetGallery.jsx during May 2026 refactor.
 */
import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API, authedFetch, verdictMeta } from "./utils";
import { reasonCopy, verdictCopy } from "./triageCopy";

function ClassifierPanel({ asset, auto, workspaceId, onAssetUpdated }) {
  const [open, setOpen] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(null);
  const [reason, setReason] = useState("");
  const consentStatus =
    (asset?.asset_metadata?.consent_status) ||
    asset?.consent_status ||
    auto?._meta?.consent_status_at_classify ||
    "unknown";
  const consentHistory = asset?.asset_metadata?.consent_history || [];

  const resetConfirm = () => {
    setConfirmStatus(null);
    setReason("");
    setConsentBusy(false);
  };

  const submitConsent = async () => {
    if (!asset?.asset_id || !confirmStatus) return;
    if ((reason || "").trim().length < 4) {
      toast.error("Add a reason (min 4 chars).");
      return;
    }
    setConsentBusy(true);
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/assets/${asset.asset_id}/consent`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ consent_status: confirmStatus, reason: reason.trim() }),
        }
      );
      if (!r.ok) {
        const errText = await r.text();
        throw new Error(errText || `HTTP ${r.status}`);
      }
      const updated = await r.json();
      toast.success(`Consent: ${confirmStatus}`);
      resetConfirm();
      if (onAssetUpdated) onAssetUpdated(updated);
    } catch (e) {
      toast.error(`Could not update consent: ${e.message}`);
      setConsentBusy(false);
    }
  };

  if (!auto) {
    return (
      <div
        className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-3 text-xs text-slate-500"
        data-testid="gallery-detail-classifier-empty"
      >
        Not yet classified. Run the classifier backfill or wait for the
        ingest-time classifier.
      </div>
    );
  }
  const vMeta = verdictMeta(auto.verdict);
  const signals = auto.signals || {};
  const meta = auto._meta || {};
  const tags = auto.tags || {};
  // Plain-English copy for the verdict reason. Falls back to humanised
  // version of raw code if a new code appears we haven't translated yet.
  const reasonMeta = reasonCopy(auto.verdict_reason);
  const verdictMetaPlain = verdictCopy(auto.verdict);
  return (
    <div
      className={`rounded-md border ${vMeta.lightBg} border-slate-200 p-3 text-xs`}
      data-testid="gallery-detail-classifier-panel"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${vMeta.color}`} />
          <span className={`font-semibold text-sm ${vMeta.textColor}`}>
            {verdictMetaPlain.headline}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs underline text-slate-600 hover:text-slate-900"
          data-testid="gallery-detail-classifier-toggle"
        >
          {open ? "Hide details" : "Details"}
        </button>
      </div>
      {/* Plain-English explanation of the reason — replaces raw
          `low_classifier_confidence` jargon. The raw code is still
          surfaced inside the Details disclosure for debugging. */}
      <div className="text-[11px] text-slate-700 mb-2 leading-snug">
        <span className="font-medium">{reasonMeta.headline}.</span>{" "}
        {reasonMeta.blurb}
      </div>

      {/* Per-asset consent toggle. Bulk-set via the folder consent
          button; per-asset override available here. */}
      <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-slate-200/60">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
          <ShieldCheck className="h-3 w-3" />
          Consent:{" "}
          <span
            className={
              consentStatus === "verified"
                ? "font-semibold text-emerald-700"
                : consentStatus === "unverified"
                ? "font-semibold text-rose-700"
                : "font-medium text-slate-700"
            }
            data-testid="gallery-detail-consent-current"
          >
            {consentStatus}
          </span>
        </div>
        {workspaceId ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-1.5 text-[10px]"
                disabled={consentBusy}
                data-testid="gallery-detail-consent-trigger"
              >
                Change
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="text-xs">
              <DropdownMenuItem onClick={() => setConfirmStatus("verified")} data-testid="gallery-detail-consent-verified">
                ✓ Verified (consent obtained)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setConfirmStatus("unverified")} data-testid="gallery-detail-consent-unverified">
                ✗ Unverified
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setConfirmStatus("unknown")} data-testid="gallery-detail-consent-unknown">
                ? Unknown (default)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      {/* Confirmation modal for per-asset consent changes. */}
      <Dialog open={!!confirmStatus} onOpenChange={(o) => { if (!o) resetConfirm(); }}>
        <DialogContent data-testid="gallery-detail-consent-modal">
          <DialogHeader>
            <DialogTitle>
              {confirmStatus === "verified"
                ? "Confirm: mark this asset's consent verified"
                : confirmStatus === "unverified"
                ? "Mark this asset's consent unverified"
                : "Reset asset consent to unknown"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            {confirmStatus === "verified" ? (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                <strong>This may flip the verdict to safe.</strong>{" "}
                Only do this if you have documented consent from every identifiable
                person in this photo.
              </div>
            ) : null}
            <label className="block text-xs">
              <span className="font-medium text-slate-700">
                Reason <span className="text-rose-600">*</span> (audit trail)
              </span>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Confirmed with subject in writing on 14 Aug; release on file."
                rows={3}
                className="mt-1 text-xs"
                data-testid="gallery-detail-consent-reason"
                autoFocus
              />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetConfirm} disabled={consentBusy} data-testid="gallery-detail-consent-cancel">
              Cancel
            </Button>
            <Button
              onClick={submitConsent}
              disabled={consentBusy || reason.trim().length < 4}
              data-testid="gallery-detail-consent-submit"
            >
              {consentBusy ? "Saving…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
        <span>rule {auto.rule_version || "—"}</span>
        <span>classifier {meta.classifier_version || "—"}</span>
        {meta.pass2_triggered ? (
          <span>2-pass · agreement: {meta.agreement || "—"}</span>
        ) : (
          <span>1-pass (no flag)</span>
        )}
      </div>

      {open ? (
        <div className="mt-3 space-y-3">
          {consentHistory && consentHistory.length > 0 ? (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                Consent audit trail
              </div>
              <ul className="space-y-1.5 text-[11px]" data-testid="gallery-detail-consent-history">
                {consentHistory.slice(-5).reverse().map((h, idx) => (
                  <li
                    key={idx}
                    className="bg-white border border-slate-200 rounded p-2"
                  >
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-slate-700">
                      <span className="font-mono text-slate-900">{h.from || "?"} → {h.to}</span>
                      <span className="text-slate-500">{h.actor_email || h.actor_id || "—"}</span>
                      <span className="text-slate-400 text-[10px]">
                        {h.at ? new Date(h.at).toLocaleString() : ""}
                      </span>
                      {h.source ? (
                        <span className="text-[10px] px-1 py-0.5 rounded bg-slate-100 text-slate-600">
                          {h.source}
                        </span>
                      ) : null}
                    </div>
                    {h.reason ? (
                      <div className="mt-1 text-slate-600 italic">"{h.reason}"</div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {tags?.description ? (
            <div className="bg-white rounded p-2 border border-slate-200">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                Description
              </div>
              <div className="text-slate-800 italic">"{tags.description}"</div>
            </div>
          ) : null}

          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
              Signals
            </div>
            <table className="w-full text-[11px]" data-testid="gallery-detail-classifier-signals">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="font-medium pr-2">Signal</th>
                  <th className="font-medium pr-2">Value</th>
                  <th className="font-medium">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(signals).map(([k, v]) => {
                  const isTrue = v?.value === true;
                  return (
                    <tr key={k} className="border-t border-slate-200 align-top">
                      <td className="pr-2 py-1 font-mono">{k}</td>
                      <td className="pr-2 py-1">
                        <span
                          className={
                            isTrue
                              ? "px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                              : "text-slate-500"
                          }
                        >
                          {String(v?.value)}
                        </span>
                      </td>
                      <td className="py-1 text-slate-600">{v?.confidence || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Reasoning for the true signals — usually the actionable bit. */}
          {Object.entries(signals).some(([, v]) => v?.value === true && v?.reasoning) ? (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                Why this verdict?
              </div>
              <ul className="space-y-1.5 list-disc list-inside text-slate-700">
                {Object.entries(signals)
                  .filter(([, v]) => v?.value === true && v?.reasoning)
                  .map(([k, v]) => (
                    <li key={k} className="break-words">
                      <span className="font-mono text-slate-900">{k}</span>:{" "}
                      <span className="text-slate-600">{v.reasoning}</span>
                    </li>
                  ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default ClassifierPanel;
export { ClassifierPanel };
